// The ReelHunt Plus and club plan screens: the paywall (#paywall) with plan cards, restore and the store's renewal
// terms, and the Plus panel under More (status, manage, restore; test-mode switches on localhost).
// What is free and what is Plus (decided 21 Oct 2026): logging, weather, map, groups and trips stay free; Plus has
// your best conditions (insights.ts), the matching-weather tip (tips.ts, push.ts), suggested spots (trip/trip.ts) and
// the season recap (season.ts). The club plan opens the full stand board for one group (groups/club.ts).
import type { Group } from "../shared/model.ts";
import type { TextKey } from "../shared/strings.ts";
import { FREE_STANDS, PRIVACY_URL, TERMS_URL } from "./config.ts";
import {
  SIM_CASES, canBuy, hasPlus, manageUrl, mode, onBillingChange, ownsClub, plans, purchase, restore, simClub, simHasClub, simOffer, simSet, state, storeName,
  type Entitlement, type EntState, type Plan, type Plans,
} from "./billing.ts";
import { $, $maybe, el, icon, toast } from "./dom.ts";
import { LOCALE, dFmtY, t } from "./i18n.ts";
import { onRender, render } from "./state.ts";
import { lsGet, lsSet } from "./util.ts";
import { claimClub, clubMine, clubOpen } from "./groups/clubplan.ts";

const dlg = $<HTMLDialogElement>("#paywall");
$("#pw-close").addEventListener("click", () => dlg.close());

export interface PaywallOpts {
  /** Why the paywall opened ("Suggested spots are part of Plus"). */
  reason?: string;
  /** The group a club plan is for. */
  group?: Group;
  /** Called after a purchase or a club plan connected to the group. */
  onDone?: () => void;
}
const money = (n: number, cur: string) => new Intl.NumberFormat(LOCALE, { style: "currency", currency: cur }).format(n);
const legalLinks = (withRestore: boolean) => el("div", { class: "pw-links" },
  withRestore && canBuy() ? el("button", { class: "linkbtn", type: "button", onclick: (e) => void doRestore(e.currentTarget) }, t("payRestore")) : null,
  el("a", { href: PRIVACY_URL, target: "_blank", rel: "noopener" }, t("privacyLink")),
  el("a", { href: TERMS_URL, target: "_blank", rel: "noopener" }, t("payTerms")));
const benefit = (text: string, later = false) => el("li", { class: later ? "later" : "" }, icon("check"), el("span", { text }));

/** Opens the paywall for Plus or for a group's club plan. */
export function openPaywall(what: Entitlement, opts: PaywallOpts = {}): void {
  if (what === "club" && !opts.group) return;
  $("#pw-title").textContent = what === "club" ? t("payClubTitle") : t("plusName");
  $("#pw-body").replaceChildren(el("p", { class: "hint", text: t("payLoading") }));
  $("#pw-foot").replaceChildren();
  if (!dlg.open) dlg.showModal();
  $("#paywall .sheet-body").scrollTop = 0;
  void (what === "club" ? fillClub(opts) : fillPlus(opts));
}

/* ---------- Plus ---------- */
async function fillPlus(opts: PaywallOpts): Promise<void> {
  const st = state().plus, back = st.kind === "expired";
  const top: (HTMLElement | null)[] = [
    opts.reason ? el("p", { class: "pw-why", text: opts.reason }) : null,
    el("p", { class: "pw-lead", text: back ? t("payWelcomeBack") : t("payPlusLead") }),
    el("ul", { class: "pw-list" }, benefit(t("payBenefitBest")), benefit(t("payBenefitTip")), benefit(t("payBenefitSpots")), benefit(t("payBenefitShelter")), benefit(t("payBenefitSeason")), benefit(t("payBenefitPhotos"), true)),
  ];
  const tail = [el("p", { class: "pw-note", text: t("payFreeNote") }), el("p", { class: "pw-note", text: t("payNoAds") })];
  if (!canBuy()) {
    $("#pw-body").replaceChildren(...top.filter(Boolean) as HTMLElement[], el("p", { class: "pw-why", text: t("payBuyInApp") }), ...tail, legalLinks(false));
    $("#pw-foot").replaceChildren(el("button", { class: "btn", type: "button", onclick: () => dlg.close() }, t("close")));
    return;
  }
  const p = await plans("plus");
  if (!dlg.open) return;
  if (!p) { noStore(top, tail); return; }
  let chosen: Plan = (p.annual ?? p.monthly)!;
  const cards = planCards(p, (plan) => { chosen = plan; legal.textContent = legalText(plan); buy.textContent = plan.trial ? t("payTrialBtn") : t("payBuyBtn"); });
  const legal = el("p", { class: "pw-legal", text: legalText(chosen) });
  const buy = el("button", { class: "btn primary", type: "button", onclick: async () => {
    buy.disabled = true;
    const trial = chosen.trial, r = await purchase("plus", chosen);
    buy.disabled = false;
    if ("ok" in r) { dlg.close(); toast(t(trial ? "payThanksTrial" : "payThanks")); render(); opts.onDone?.(); }
    else if ("error" in r) toast(t(r.error));
  } }, chosen.trial ? t("payTrialBtn") : t("payBuyBtn"));
  $("#pw-body").replaceChildren(...top.filter(Boolean) as HTMLElement[], cards,
    ...(p.founding ? [el("p", { class: "pw-founding" }, icon("star"), el("span", { text: t("payFounding") }))] : []),
    ...tail, legal, legalLinks(true));
  $("#pw-foot").replaceChildren(buy);
}
function legalText(plan: Plan): string {
  const unit = t(plan.kind === "year" ? "unitYear" : "unitMonth");
  return (plan.trial ? t("payThenPrice", plan.price, unit) : t("payCharged", plan.price, unit, storeName())) + " " + t("payAutoRenew", storeName()) + (plan.trial ? " " + t("payTrialCancel") : "");
}
function planCards(p: Plans, onPick: (plan: Plan) => void): HTMLElement {
  const box = el("div", { class: "pw-plans", role: "radiogroup", "aria-label": t("plusName") });
  const card = (plan: Plan, checked: boolean) => {
    const yr = plan.kind === "year";
    const sub = [yr && plan.perMonth != null ? t("payAboutMonth", money(plan.perMonth, plan.currency)) : "", yr && plan.save ? t("paySave", plan.save) : ""].filter(Boolean).join(" · ");
    return el("button", { class: "pw-plan", type: "button", role: "radio", "aria-checked": String(checked), onclick: (e) => {
      box.querySelectorAll(".pw-plan").forEach((c) => c.setAttribute("aria-checked", String(c === e.currentTarget)));
      onPick(plan);
    } },
      plan.trial ? el("span", { class: "pw-tag", text: t("payFirstFree") }) : null,
      el("span", { class: "radio", "aria-hidden": "true" }),
      el("span", { class: "pmain" }, el("b", { text: t(yr ? "payYear" : "payMonth") }), sub ? el("small", { class: "num", text: sub }) : null,
        p.founding ? el("small", { text: t("payFoundingBadge") }) : null),
      el("span", { class: "pprice num", text: `${plan.price} / ${t(yr ? "unitYear" : "unitMonth")}` }));
  };
  if (p.annual) box.append(card(p.annual, true));
  if (p.monthly) box.append(card(p.monthly, !p.annual));
  return box;
}
function noStore(top: (HTMLElement | null)[], tail: HTMLElement[]): void {
  $("#pw-body").replaceChildren(...top.filter(Boolean) as HTMLElement[], el("p", { class: "pw-why", text: t("payNoStore") }), ...tail, legalLinks(true));
  $("#pw-foot").replaceChildren(el("button", { class: "btn", type: "button", onclick: () => dlg.close() }, t("close")));
}

/* ---------- club plan ---------- */
async function fillClub(opts: PaywallOpts): Promise<void> {
  const g = opts.group!;
  const top: (HTMLElement | null)[] = [
    opts.reason ? el("p", { class: "pw-why", text: opts.reason }) : null,
    el("p", { class: "pw-lead", text: t("payClubLead", g.name) }),
    el("ul", { class: "pw-list" }, benefit(t("payClubB1", FREE_STANDS)), benefit(t("payClubB2")), benefit(t("payClubB3"))),
  ];
  const tail = [el("p", { class: "pw-note", text: t("payClubNote") }), el("p", { class: "pw-note", text: t("payNoAds") })];
  const close = () => el("button", { class: "btn", type: "button", onclick: () => dlg.close() }, t("close"));
  if (clubOpen(g) && !clubMine(g)) {
    $("#pw-body").replaceChildren(...top.filter(Boolean) as HTMLElement[], el("p", { class: "pw-why", text: t("payClubAlready") }));
    $("#pw-foot").replaceChildren(close());
    return;
  }
  const connect = async (btn: HTMLButtonElement) => {
    btn.disabled = true;
    const r = await claimClub(g);
    btn.disabled = false;
    if (r === "ok") { dlg.close(); toast(t("payClubThanks", g.name)); opts.onDone?.(); }
    else toast(t(r === "already" ? "payClubAlready" : "payClubClaimFailed"));
  };
  // you already pay for a club plan (for another group, or bought on another phone): move it here
  if (ownsClub()) {
    $("#pw-body").replaceChildren(...top.filter(Boolean) as HTMLElement[], el("p", { class: "pw-note", text: t("payClubMoveNote") }), ...tail, legalLinks(true));
    $("#pw-foot").replaceChildren(el("button", { class: "btn primary", type: "button", onclick: (e) => void connect(e.currentTarget) }, t("payClubMove")));
    return;
  }
  if (!canBuy()) {
    $("#pw-body").replaceChildren(...top.filter(Boolean) as HTMLElement[], el("p", { class: "pw-why", text: t("payBuyInApp") }), ...tail, legalLinks(false));
    $("#pw-foot").replaceChildren(close());
    return;
  }
  const p = await plans("club");
  if (!dlg.open) return;
  const plan = p?.annual ?? p?.monthly;
  if (!plan) { noStore(top, tail); return; }
  const buy = el("button", { class: "btn primary", type: "button", onclick: async () => {
    buy.disabled = true;
    const r = await purchase("club", plan);
    if ("ok" in r) await connect(buy);
    else if ("error" in r) toast(t(r.error));
    buy.disabled = false;
  } }, `${t("payClubBtn")} · ${plan.price}`);
  $("#pw-body").replaceChildren(...top.filter(Boolean) as HTMLElement[],
    el("div", { class: "pw-plans" }, el("div", { class: "pw-plan", "aria-checked": "true" }, el("span", { class: "radio", "aria-hidden": "true" }),
      el("span", { class: "pmain" }, el("b", { text: t("payClubTitle") }), el("small", { text: g.name })),
      el("span", { class: "pprice num", text: `${plan.price} / ${t("unitYear")}` }))),
    ...tail, el("p", { class: "pw-legal", text: t("payClubCharged", plan.price, storeName()) + " " + t("payAutoRenew", storeName()) }), legalLinks(true));
  $("#pw-foot").replaceChildren(buy);
}

async function doRestore(btn: HTMLButtonElement): Promise<void> {
  btn.disabled = true;
  const r = await restore();
  btn.disabled = false;
  if ("error" in r) { toast(t(r.error)); return; }
  toast(t(r.restored ? "payRestored" : "payNothingToRestore"));
  if (r.restored && dlg.open && hasPlus()) dlg.close();
  render();
}

/* ---------- the Plus panel under More ---------- */
const day = (ms: number | null) => (ms ? dFmtY.format(ms) : "");
function statusLines(p: EntState): HTMLElement[] {
  const lines: HTMLElement[] = [];
  const planName = t(p.plan === "year" ? "payYear" : "payMonth") + (p.founding ? " · " + t("payFoundingBadge") : "");
  if (p.kind === "none") lines.push(el("p", { text: t("plusNone") }));
  if (p.kind === "trial") lines.push(el("b", { text: t("plusTrial", day(p.expiresAt)) }), el("p", { text: p.willRenew ? t("plusTrialThen") : t("plusEnds", day(p.expiresAt)) }));
  if (p.kind === "active") lines.push(el("b", { text: t("plusActive", planName) }), el("p", { class: "num", text: p.willRenew ? t("plusRenews", day(p.expiresAt)) : t("plusEnds", day(p.expiresAt)) }));
  if (p.kind === "expired") lines.push(el("p", { text: t("plusExpired", day(p.expiresAt)) }));
  if (p.billingIssue) lines.push(el("p", { class: "hint warn", text: t("plusBillingIssue") }));
  return lines;
}
export function renderPlusPanel(): void {
  const box = $maybe("#plus-panel");
  if (!box) return;
  const s = state(), title = el("h2", {}, t("plusName"));
  if (mode === "off" || s.offline) { box.replaceChildren(title, el("p", { text: t("plusOff") })); return; }
  const p = s.plus, on = p.kind === "trial" || p.kind === "active";
  const club = s.club.kind === "none" ? null
    : el("p", { class: "num", text: s.club.kind === "expired" ? t("plusClubEnded", day(s.club.expiresAt)) : t("plusClubOn", day(s.club.expiresAt)) });
  const btns = el("div", { class: "btns" },
    !on ? el("button", { class: "btn primary", type: "button", onclick: () => openPaywall("plus") }, t(p.kind === "none" && canBuy() ? "plusTry" : "plusSee")) : null,
    (on || s.club.kind === "trial" || s.club.kind === "active") && canBuy() ? el("a", { class: "btn" + (on ? " primary" : ""), href: manageUrl(), target: "_blank", rel: "noopener" }, t("plusManage")) : null,
    canBuy() ? el("button", { class: "btn", type: "button", onclick: (e) => void doRestore(e.currentTarget) }, t("payRestore")) : null);
  box.replaceChildren(title, el("div", { class: "plusstate" }, ...statusLines(p)), ...(club ? [club] : []),
    ...(mode === "web" ? [el("p", { class: "hint", text: t("payBuyInApp") })] : []), btns,
    el("p", { class: "hint" }, t("payNoAds"), " ", el("a", { href: TERMS_URL, target: "_blank", rel: "noopener" }, t("payTerms"))),
    ...(mode === "sim" ? [testMode()] : []));
}
/** Localhost only: switch between the cases without a store. */
function testMode(): HTMLElement {
  return el("div", { class: "plustest" }, el("span", { class: "lbl", text: t("plusTest") }),
    el("div", { class: "chips" }, ...SIM_CASES.map((c) => el("button", { class: "chip", type: "button", "aria-pressed": String(simCase() === c),
      onclick: () => { lsSet("simCase", c); void simSet(c, simOffer()); } }, t(`plusTest_${c}` as TextKey)))),
    el("div", { class: "chips" },
      el("button", { class: "chip", type: "button", "aria-pressed": String(simOffer() === "founding"), onclick: () => void simSet(simCase(), simOffer() === "founding" ? "standard" : "founding") }, t("plusTestOffer")),
      el("button", { class: "chip", type: "button", "aria-pressed": String(simHasClub()), onclick: () => void simClub(!simHasClub()) }, t("plusTestClub"))));
}
const simCase = () => { const c = lsGet("simCase", "none"); return (SIM_CASES as readonly string[]).includes(c) ? c as (typeof SIM_CASES)[number] : "none"; };

onRender(renderPlusPanel);

/* ---------- start ---------- */
let hadPlus = hasPlus();
onBillingChange(() => {
  renderPlusPanel();
  if (hasPlus() !== hadPlus) { hadPlus = hasPlus(); render(); } // gated parts of the screen redraw
});
